import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import api from '@/lib/api';

/* Infrarotkabine (16.10.2026): Wer liegt heute und morgen in der Kabine? Gebucht wird nur in der App.
   Die Karte zeigt nur an — ein Klick auf den Namen öffnet den Lead, NIE eine Aktion (anders als die
   Anwesend-Liste daneben: dort ist jede Zeile ein Auschecken-Knopf).
   Uhr, „vorbei“ und „läuft“ rechnet der Server (GET /api/infrarot/buchungen, Recht checkin.view wie /empfang);
   „läuft“ heißt dort: jemand liegt gerade in der Kabine. Ob der Name ein Link ist, sagt `oeffnen` je Buchung
   (dieselbe Regel wie die Lead-Seite) — sonst sprang ein Konto ohne Zugriff wortlos auf /pipeline.
   Ohne das Recht bleibt die Karte weg. */

const AKTUALISIEREN_MS = 60 * 1000;

function tagTitel(iso, i) {
  const datum = new Date(`${iso}T12:00:00`).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
  if (i === 0) return `Heute · ${datum}`;
  if (i === 1) return `Morgen · ${datum}`;
  return datum;
}

function SlotZeile({ s }) {
  const gedimmt = s.vorbei && !s.laeuft;
  return (
    <div
      className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 ${s.laeuft ? 'border-marke/40 bg-marke/5' : 'border-border/60'} ${gedimmt ? 'opacity-50' : ''}`}
      data-testid={`infrarot-slot-${s.slot}`}
    >
      <span className="text-sm tabular-nums text-muted-foreground shrink-0">{s.slot}{s.ende ? `–${s.ende}` : ''}</span>
      <span className="min-w-0 flex items-center gap-2 justify-end">
        {s.buchungen.length === 0 ? (
          <span className="text-sm text-muted-foreground">frei</span>
        ) : s.buchungen.map((b, i) => (
          <span key={b.id || i} className="min-w-0 truncate">
            {i > 0 && <span className="text-muted-foreground">, </span>}
            {b.member_id && b.oeffnen ? (
              <Link to={`/leads/${b.member_id}`} state={{ from: '/empfang' }} className="text-sm font-medium text-marke hover:underline" data-testid={`infrarot-name-${b.member_id}`}>
                {b.name}
              </Link>
            ) : <span className="text-sm font-medium">{b.name}</span>}
          </span>
        ))}
        {s.laeuft && <span className="text-[10px] font-semibold uppercase tracking-wide text-marke bg-marke/10 rounded px-1.5 py-0.5 shrink-0">läuft</span>}
      </span>
    </div>
  );
}

export default function InfrarotKarte() {
  // null = lädt, false = kein Recht (Karte weg), 'fehler' = erstes Laden gescheitert
  const [d, setD] = useState(null);
  const laden = useCallback(() => api.get('/infrarot/buchungen')
    .then((r) => setD(r.data))
    .catch((e) => setD((alt) => {
      if (e?.response?.status === 403) return false;
      return alt && typeof alt === 'object' ? alt : 'fehler';   // beim Nachladen den letzten Stand stehen lassen
    })), []);
  useEffect(() => {
    laden();
    const t = setInterval(laden, AKTUALISIEREN_MS);
    return () => clearInterval(t);
  }, [laden]);

  if (d === false) return null;

  return (
    <Card className="border-border/60" data-testid="infrarot-karte">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <Flame className="w-5 h-5 text-marke" />
          <h2 className="text-lg font-semibold">Infrarotkabine</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-3">Gebucht wird in der App. Ein farbiger Name öffnet das Mitglied.</p>
        {d === null ? (
          <p className="text-center text-sm text-muted-foreground py-6">Lädt…</p>
        ) : d === 'fehler' ? (
          <p className="text-center text-sm text-muted-foreground py-6" data-testid="infrarot-fehler">Die Buchungen der Infrarotkabine konnten gerade nicht geladen werden.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {d.tage.map((t, i) => {
              const gebucht = t.slots.filter((s) => s.buchungen.length > 0).length;
              return (
                <div key={t.datum} className="space-y-2" data-testid={`infrarot-tag-${t.datum}`}>
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold">{tagTitel(t.datum, i)}</h3>
                    <span className="text-xs text-muted-foreground">{gebucht === 0 ? 'nichts gebucht' : `${gebucht} gebucht`}</span>
                  </div>
                  {t.slots.map((s) => <SlotZeile key={s.slot} s={s} />)}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
